import { useQuery, type QueryClient } from "@tanstack/react-query";

import { _isSafePayloadPath, apiFetchPayloadText } from "@/api/client";

/**
 * Whether a server-supplied `download_url` is one this app will fetch WITH THE
 * USER'S CREDENTIALS.
 *
 * The check itself is `client.ts`'s `_isSafePayloadPath` — the same pin
 * `apiDownload` and `apiFetchPayloadText` already apply at the boundary
 * (flowstack#624), so this adds no second opinion about what is safe; there is
 * exactly one such rule and it lives there. What this name adds is the ability
 * to ask the question BEFORE fetching, which two callers need:
 *
 * - **A view can render a non-conforming address as its own state** instead of
 *   as a generic "could not load", so a poisoned or mis-generated URL is
 *   visible as what it is rather than looking like a network fault.
 * - **Speculative prefetching can skip it.** A prefetch swallows its outcome by
 *   design (it is work for a point the user may never reach), and a rejected
 *   address is the one outcome that must not be swallowed — so it is never
 *   started (Rupert PR-1193 r0).
 *
 * A conforming path is origin-relative by construction, so a credentialed fetch
 * can only target the configured API origin. `download_url` is server-response
 * data crossing a trust boundary: this PR declines to trust the server's
 * `geometry_source` vocabulary, and a URL gets the same standard.
 */
export function isViewablePayloadUrl(downloadUrl: string): boolean {
  return _isSafePayloadPath(downloadUrl);
}

/**
 * Largest structure the in-page viewer will fetch and parse, in bytes.
 *
 * Deliberately far below the server's download cap
 * (`FLOWSTACK_MAX_PAYLOAD_DOWNLOAD_BYTES`, 64 MiB by default): that number
 * bounds what the API process will *serve*, while this one bounds what a
 * browser tab will hold as text, hand to a parser, and turn into WebGL
 * geometry — three copies, none of which the download path pays for.
 *
 * 8 MiB clears the structures FlowStack actually catalogues by orders of
 * magnitude: the ligand-scale SDF/mol2/pdb files that `Molecule`,
 * `ConformerEnsemble` and `ProteinStructure` carry are kilobytes to low
 * megabytes. What it refuses is the trajectory-scale payload that would lock
 * the tab up rather than draw — and it refuses it with a message, which is the
 * point. Compiled in rather than configured because it is a property of the
 * client's memory and the renderer, not of a deployment; the server-side knob
 * stays the operational one.
 */
export const MAX_VIEWER_PAYLOAD_BYTES = 8 * 1024 * 1024;

export function payloadTextQueryKey(downloadUrl: string) {
  return ["artifacts", "payload-text", downloadUrl] as const;
}

/**
 * Read one artifact payload as text for in-page rendering.
 *
 * Disabled until `downloadUrl` is non-null, so a page can call it
 * unconditionally while the artifact detail is still loading or while the
 * artifact turns out to have no viewable payload at all.
 *
 * `staleTime: Infinity` because the payload is content-addressed: the URL
 * embeds the artifact's `content_hash`, so the bytes behind it cannot change.
 * Re-fetching one would be pure cost.
 */
export function useArtifactPayloadText(downloadUrl: string | null) {
  return useQuery({
    queryKey: payloadTextQueryKey(downloadUrl ?? ""),
    enabled: downloadUrl !== null && downloadUrl !== "",
    queryFn: () => apiFetchPayloadText(downloadUrl as string, MAX_VIEWER_PAYLOAD_BYTES),
    staleTime: Infinity,
    // A 413 or a 404 is a property of the artifact, not a transient fault;
    // retrying re-fetches bytes that will be refused identically.
    retry: false,
  });
}

/**
 * Warm the cache for a payload the user has not asked for yet (VIZ U5.2b's
 * neighbour prefetch).
 *
 * Same key, same fetcher and the same `staleTime: Infinity` as
 * {@link useArtifactPayloadText} — so a prefetched neighbour is served from
 * cache the instant the scrub reaches it, and a point already in cache costs
 * nothing (`prefetchQuery` is a no-op against fresh data, and `Infinity` makes
 * every cached payload fresh forever, which is sound because the URL embeds a
 * content hash).
 *
 * Errors are swallowed deliberately: this is speculative work for a point the
 * user may never visit, and a 404 on a neighbour must not surface anywhere. If
 * the scrub does reach that point, `useArtifactPayloadText` asks again and
 * renders the failure THEN, attached to the point it belongs to.
 *
 * The ONE outcome not swallowed is a rejected address, and it is not swallowed
 * because it never happens here: a `download_url` that fails
 * {@link isViewablePayloadUrl} is not prefetched at all. Letting the guard fire
 * inside a promise whose rejection is discarded would make "the server sent a
 * URL shape this app refuses" the quietest event on the page.
 */
export function prefetchArtifactPayloadText(client: QueryClient, downloadUrl: string): void {
  if (!isViewablePayloadUrl(downloadUrl)) return;
  void client
    .prefetchQuery({
      queryKey: payloadTextQueryKey(downloadUrl),
      queryFn: () => apiFetchPayloadText(downloadUrl, MAX_VIEWER_PAYLOAD_BYTES),
      staleTime: Infinity,
      retry: false,
    })
    .catch(() => undefined);
}
